/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import type { IntlShape } from 'react-intl-next/src/types';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { ELEMENT_BROWSER_ID } from '../../element-browser';
import { fullPageMessages } from '../../messages';
import { mediaInsertMessages } from '../../messages/media-insert';
import type { EditorAppearance } from '../../types';
import type { UseStickyToolbarType } from '../../ui';
import { EDIT_AREA_ID } from '../../ui';

export interface KeyDownHandlerContext {
	handleArrowLeft: () => void;
	handleArrowRight: () => void;
	handleTab: () => void;
}

/*
 **  The context is used to handle the keydown events of submenus.
 **  Because the keyboard navigation is explicitly managed for main toolbar items
 **  Few key presses such as Tab,Arrow Right/Left need ot be handled here via context
 */
export const KeyDownHandlerContext = React.createContext<KeyDownHandlerContext>({
	handleArrowLeft: () => {},
	handleArrowRight: () => {},
	handleTab: () => {},
});

const centeredToolbarContainer = css({
	display: 'flex',
	width: '100%',
	alignItems: 'center',
});

/**
 * This component is a wrapper of main toolbar which listens to keydown events of children
 * and handles left/right arrow key navigation for all focusable elements
 * @param
 * @returns
 */
export const ToolbarArrowKeyNavigationProvider = ({
	children,
	editorView,
	childComponentSelector,
	handleEscape,
	disableArrowKeyNavigation,
	isShortcutToFocusToolbar,
	editorAppearance,
	useStickyToolbar,
	intl,
}: {
	// Selector is used to filterout the keyevents originated outside of toolbars/any child component
	childComponentSelector: string;
	children: ReactNode;
	disableArrowKeyNavigation?: boolean;
	editorAppearance?: EditorAppearance;
	editorView?: EditorView;
	handleEscape?: (event: KeyboardEvent) => void;
	intl: IntlShape;
	isShortcutToFocusToolbar?: (event: KeyboardEvent) => boolean;
	useStickyToolbar?: UseStickyToolbarType;
}) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const selectedItemIndex = useRef(0);

	const incrementIndex = useCallback((list: HTMLElement[]) => {
		let index = 0;
		if (document.activeElement) {
			// Ignored via go/ees005
			// eslint-disable-next-line @atlaskit/editor/no-as-casting
			index = list.indexOf(document.activeElement as HTMLElement);
			index = (index + 1) % list.length;
		}
		selectedItemIndex.current = index;
	}, []);

	const decrementIndex = useCallback((list: HTMLElement[]) => {
		let index = 0;
		if (document.activeElement) {
			// Ignored via go/ees005
			// eslint-disable-next-line @atlaskit/editor/no-as-casting
			index = list.indexOf(document.activeElement as HTMLElement);
			index = (list.length + index - 1) % list.length;
		}
		selectedItemIndex.current = index;
	}, []);

	const handleArrowRightMemoized = useCallback((): void => {
		const filteredFocusableElements = getFilteredFocusableElements(wrapperRef?.current);

		incrementIndex(filteredFocusableElements);
		filteredFocusableElements[selectedItemIndex.current]?.focus();
	}, [incrementIndex]);

	const handleArrowLeftMemoized = useCallback((): void => {
		const filteredFocusableElements = getFilteredFocusableElements(wrapperRef?.current);

		decrementIndex(filteredFocusableElements);
		filteredFocusableElements[selectedItemIndex.current]?.focus();
	}, [decrementIndex]);

	const handleTabMemoized = useCallback((): void => {
		const filteredFocusableElements = getFilteredFocusableElements(wrapperRef?.current);
		filteredFocusableElements[selectedItemIndex.current]?.focus();
	}, []);

	const handleTabLocal = (): void => {
		const filteredFocusableElements = getFilteredFocusableElements(wrapperRef?.current);
		filteredFocusableElements.forEach((element) => {
			element.setAttribute('tabindex', '-1');
		});
		filteredFocusableElements[selectedItemIndex.current].setAttribute('tabindex', '0');
	};

	const focusAndScrollToElement = (element: HTMLElement, scrollToElement = true): void => {
		if (scrollToElement) {
			element?.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
				inline: 'nearest',
			});
		}
		element.focus();
	};

	const submenuKeydownHandleContext = useMemo(
		() => ({
			handleArrowLeft: handleArrowLeftMemoized,
			handleArrowRight: handleArrowRightMemoized,
			handleTab: handleTabMemoized,
		}),
		[handleArrowLeftMemoized, handleArrowRightMemoized, handleTabMemoized],
	);

	useLayoutEffect(() => {
		if (!wrapperRef.current || disableArrowKeyNavigation) {
			return;
		}
		const { current: element } = wrapperRef;

		/**
		 * To handle the key events on the list
		 * @param event
		 */
		const handleKeyDown = (event: KeyboardEvent): void => {
			// To trap the focus inside the horizontal toolbar for left and right arrow keys
			const targetElement = event.target;

			if (
				targetElement instanceof HTMLElement &&
				!targetElement.closest(`${childComponentSelector}`)
			) {
				return;
			}

			if (
				(targetElement instanceof HTMLElement &&
					document
						.querySelector(
							'[data-role="droplistContent"], [data-test-id="color-picker-menu"], [data-emoji-picker-container="true"]',
						)
						?.contains(targetElement)) ||
				(targetElement instanceof HTMLElement &&
					document.querySelector('[data-test-id="color-picker-menu"]')?.contains(targetElement)) ||
				event.key === 'ArrowUp' ||
				event.key === 'ArrowDown' ||
				disableArrowKeyNavigation
			) {
				return;
			}
			const menuWrapper = document.querySelector('.menu-key-handler-wrapper');
			if (menuWrapper) {
				// if menu wrapper exists, then a menu is open and arrow keys will be handled by MenuArrowKeyNavigationProvider
				return;
			}

			const elementBrowser = wrapperRef?.current?.querySelector(`#${ELEMENT_BROWSER_ID}`);
			if (elementBrowser) {
				// if element browser is open, then arrow keys will be handled by MenuArrowKeyNavigationProvider
				return;
			}

			const filteredFocusableElements = getFilteredFocusableElements(wrapperRef?.current);
			if (!filteredFocusableElements || filteredFocusableElements?.length === 0) {
				return;
			}

			// If the target element is the media picker then navigation is handled by the media picker
			if (
				targetElement instanceof HTMLElement &&
				targetElement.closest(
					`[aria-label="${intl.formatMessage(mediaInsertMessages.mediaPickerPopupAriaLabel)}"]`,
				)
			) {
				return;
			}

			if (targetElement instanceof HTMLElement && !wrapperRef.current?.contains(targetElement)) {
				selectedItemIndex.current = -1;
			} else {
				selectedItemIndex.current =
					targetElement instanceof HTMLElement &&
					filteredFocusableElements.indexOf(targetElement) > -1
						? filteredFocusableElements.indexOf(targetElement)
						: selectedItemIndex.current;
			}

			// do not scroll to focused element for sticky toolbar when navigating with arrows to avoid unnesessary scroll jump
			const allowScrollToElement = !(editorAppearance === 'comment' && !!useStickyToolbar);

			switch (event.key) {
				case 'ArrowRight':
					incrementIndex(filteredFocusableElements);
					focusAndScrollToElement(
						filteredFocusableElements[selectedItemIndex.current],
						allowScrollToElement,
					);
					event.preventDefault();
					break;
				case 'ArrowLeft':
					decrementIndex(filteredFocusableElements);
					focusAndScrollToElement(
						filteredFocusableElements[selectedItemIndex.current],
						allowScrollToElement,
					);
					event.preventDefault();
					break;
				case 'Tab':
					handleTabLocal();
					break;
				case 'Escape':
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					handleEscape!(event);
					break;
				default:
			}
		};

		const globalKeyDownHandler = (event: KeyboardEvent): void => {
			// To focus the first element in the toolbar
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			if (isShortcutToFocusToolbar!(event)) {
				const filteredFocusableElements = getFilteredFocusableElements(wrapperRef?.current);
				filteredFocusableElements[0]?.focus();

				// Button component from DS removes focus ring when :focus:not(:focus-visible) is true
				// Since here we programmatically focus the first button in the toolbar (as suppose to keyboard navigation), browser does not always force focus-visible to true
				// which is why the first button in the toolbar is not shown with focus ring
				// The workaround is add a new classname so we add back focus ring when the button is focused
				if (filteredFocusableElements[0]?.tagName === 'BUTTON') {
					filteredFocusableElements[0].classList.add('first-floating-toolbar-button');
				}
				filteredFocusableElements[0]?.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'nearest',
				});
			}
		};

		// Ignored via go/ees005
		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		element?.addEventListener('keydown', handleKeyDown);
		const editorViewDom = editorView?.dom as HTMLElement | undefined;
		if (isShortcutToFocusToolbar) {
			// Ignored via go/ees005
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			editorViewDom?.addEventListener('keydown', globalKeyDownHandler);
		}
		return () => {
			// Ignored via go/ees005
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			element?.removeEventListener('keydown', handleKeyDown);
			if (isShortcutToFocusToolbar) {
				// Ignored via go/ees005
				// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
				editorViewDom?.removeEventListener('keydown', globalKeyDownHandler);
			}
		};
	}, [
		selectedItemIndex,
		wrapperRef,
		editorView,
		disableArrowKeyNavigation,
		handleEscape,
		childComponentSelector,
		incrementIndex,
		decrementIndex,
		isShortcutToFocusToolbar,
		editorAppearance,
		useStickyToolbar,
		intl,
	]);

	return (
		<div
			css={editorAppearance === 'comment' && centeredToolbarContainer}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className="custom-key-handler-wrapper"
			ref={wrapperRef}
			role="toolbar"
			aria-label={intl.formatMessage(fullPageMessages.toolbarLabel)}
			aria-controls={EDIT_AREA_ID}
		>
			<KeyDownHandlerContext.Provider value={submenuKeydownHandleContext}>
				{children}
			</KeyDownHandlerContext.Provider>
		</div>
	);
};

function getFocusableElements(rootNode: HTMLElement | null): Array<HTMLElement> {
	if (!rootNode) {
		return [];
	}
	const focusableModalElements =
		(rootNode.querySelectorAll(
			'a[href], button:not([disabled]), textarea, input, select, div[tabindex="-1"], div[tabindex="0"]',
		) as NodeListOf<HTMLElement>) || [];
	return Array.from(focusableModalElements);
}

function isElementOrAncestorHiddenOrDisabled(
	element: HTMLElement,
	rootNode: HTMLElement | null,
): boolean {
	let currentElement: HTMLElement | null = element;

	while (currentElement && currentElement !== rootNode && currentElement !== document.body) {
		const style = window.getComputedStyle(currentElement);

		// Check if current element is hidden
		if (style.visibility === 'hidden' || style.display === 'none') {
			return true;
		}

		// Check if current element is disabled
		if (
			currentElement.hasAttribute('disabled') ||
			currentElement.getAttribute('aria-disabled') === 'true' ||
			(
				currentElement as
					| HTMLButtonElement
					| HTMLInputElement
					| HTMLSelectElement
					| HTMLTextAreaElement
			).disabled === true
		) {
			return true;
		}

		// Move to parent element
		currentElement = currentElement.parentElement;
	}

	return false;
}

function getFilteredFocusableElements(rootNode: HTMLElement | null): Array<HTMLElement> {
	// The focusable elements from child components such as dropdown menus / popups are ignored
	return getFocusableElements(rootNode).filter((elm) => {
		// Check if element or any ancestor is hidden or disabled
		const isHiddenOrDisabled = isElementOrAncestorHiddenOrDisabled(elm, rootNode);

		return (
			!elm.closest('[data-role="droplistContent"]') &&
			!elm.closest('[data-emoji-picker-container="true"]') &&
			!elm.closest('[data-test-id="color-picker-menu"]') &&
			!elm.closest('.scroll-buttons') &&
			!isHiddenOrDisabled
		);
	});
}
